import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { ArrowBig } from '@/svgs';
import { colors, medium, mediumInter, flexBetween, getHoverColor } from '@/styles';
import { MetroEvent, EnvironmentHelper } from '@/classes';
import { ClickableElement } from '@/atoms';
import { apiCall, allSettledPolyfill, useId } from '@/functions';

/**
 * VideoCtaBanner component
 *
 * @version 1.0.0
 * @param {import('./VideoCtaBanner').VideoCtaBannerProps} props
 */
const VideoCtaBanner = ({
  className,
  title,
  link,
  metroHelper,
  color,
  setActiveListener,
  videoPlayMetadata,
  currentTime,
  player,
  videoPlayerRefs: { videoRef, videoPlayerRef },
  isRebrand,
}) => {
  const [active, setActive] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(null);
  const collapseRef = useRef(null);
  const [ctaLinkId] = useId(1, 'cta-link-');

  const sendCtaClickedAnalytic = useCallback(async () => {
    if (!metroHelper || !videoPlayMetadata) {
      return;
    }
    const { videoId } = videoPlayMetadata;
    const ctaBannerClickedMetroEvent = new MetroEvent(
      'video_calls_to_action.frontend.playback.cta_banner.clicked',
      null,
      null,
      videoId,
    );

    const response = await metroHelper.sendMetrics([ctaBannerClickedMetroEvent]);
    if (response.status !== 200) {
      throw new Error(`Error sending CTA banner click metric: ${response.body}`);
    }
  }, [metroHelper, videoPlayMetadata]);

  const sendCtaClickedReaction = useCallback(async () => {
    if (!videoPlayMetadata) {
      return;
    }
    const { jobId, videoViewId, videoId, browserViewerId } = videoPlayMetadata;
    const reactionServiceUrl = EnvironmentHelper.getReactionServiceUrl();
    const url = new URL(`/interaction/${jobId}/${videoId}/cta`, reactionServiceUrl);
    url.searchParams.append('videoViewId', videoViewId);
    url.searchParams.append('browserViewerId', browserViewerId);
    url.searchParams.append('ts', videoTimestamp);

    // reactions is a lambda at the moment, so extend the timeout.
    await apiCall(url.toString(), { options: { method: 'POST' }, timeout: 3000 });
  }, [videoPlayMetadata, videoTimestamp]);

  const handleCtaClick = useCallback(async () => {
    await allSettledPolyfill([sendCtaClickedReaction(), sendCtaClickedAnalytic()]);
  }, [sendCtaClickedReaction, sendCtaClickedAnalytic]);

  const handleArrowSvgClick = useCallback(() => {
    setActive(!active);
  }, [active]);

  const scaleCtaBanner = useCallback(() => {
    if (!videoRef.current || !videoPlayerRef.current) {
      return;
    }

    const { current: videoNode } = videoRef;

    /* eslint-disable-next-line no-restricted-globals */
    const rootPlayerNode = videoPlayerRef?.current?.getRootNode() ?? document;
    const ctaBannerNode = rootPlayerNode.querySelector('.vjs-cta-banner');
    if (ctaBannerNode === null) {
      return;
    }

    // As of now, this is an arbitrary width defined on a design file that cta's should scale off of
    const baseFrameWidth = 723;
    let scaleFactor = videoNode.offsetWidth / baseFrameWidth;
    // EX-8694: For iOS devices, scaleFactor was being set to 0 preventing the CTA from displaying
    if (scaleFactor === 0) {
      scaleFactor = 1;
    }

    ctaBannerNode.style.transform = `scale(${scaleFactor})`;
  }, [videoRef, videoPlayerRef]);

  useEffect(() => {
    if (setActiveListener) {
      setActive(true);
    }
  }, [setActiveListener]);

  /**
   * Must target collapsible-content-wrapper's child to get parent's height
   *    when initially rendered as 'off'.
   * Only set on Mount
   */
  useEffect(() => {
    setVideoTimestamp(currentTime);
    const { current: collapseNode } = collapseRef;

    collapseNode.style.width = `${collapseNode.firstChild.offsetWidth}px`;
  }, [currentTime]);

  useEffect(() => {
    scaleCtaBanner();
    player.on('fullscreenchange', scaleCtaBanner);
    return () => {
      player.off('fullscreenchange', scaleCtaBanner);
    };
  }, [player, scaleCtaBanner]);

  return (
    <div className={className}>
      <div
        className={`collapsible-content-wrapper ${active ? '' : 'hidden'}`}
        ref={collapseRef}
        id={ctaLinkId}
      >
        <a href={link} onClick={handleCtaClick} target="_blank" rel="noopener noreferrer">
          {title}
        </a>
      </div>
      <ClickableElement
        onClick={handleArrowSvgClick}
        onKeyDown={() => {}}
        tagName="button"
        aria-label={`${active ? 'Hide call to action' : 'Reveal call to action'}`}
        aria-controls={ctaLinkId}
        aria-expanded={!!active}
      >
        <ArrowBig fill={colors.fog} direction={`${active ? 'right' : 'left'}`} />
      </ClickableElement>
    </div>
  );
};

VideoCtaBanner.propTypes = {
  /** Emotion base style */
  className: PropTypes.string,
  /** The text of the banner */
  title: PropTypes.string.isRequired,
  /** The link clicking on the banner will call */
  link: PropTypes.string.isRequired,
  /** The metroHelper instance, if logging usage to metro */
  // metroHelper: PropTypes.instanceOf(MetroHelper),
  metroHelper: PropTypes.shape(),
  /* The hex value to be used for the banner */
  color: PropTypes.string,
  /* Used to set state from outside, commonly used to animate "open" */
  setActiveListener: PropTypes.bool,
  /** Video Played MetaData */
  videoPlayMetadata: PropTypes.shape({
    videoViewId: PropTypes.string,
    videoId: PropTypes.string,
    jobId: PropTypes.string,
    browserViewerId: PropTypes.string,
  }),
  currentTime: PropTypes.number,
  player: PropTypes.shape().isRequired,
  videoPlayerRefs: PropTypes.shape({
    videoRef: PropTypes.shape(),
    videoPlayerRef: PropTypes.shape(),
  }).isRequired,
  isRebrand: PropTypes.bool,
};

VideoCtaBanner.defaultProps = {
  className: '',
  metroHelper: null,
  color: colors.taosSky,
  setActiveListener: false,
  videoPlayMetadata: null,
  currentTime: null,
  isRebrand: false,
};

const styledCtaBanner = styled(VideoCtaBanner)`
  ${({ isRebrand }) => (isRebrand ? mediumInter() : medium())}
  ${flexBetween()}
  width: 100%;
  height: 37px;
  border-radius: 2px;
  transform-origin: top right;

  .collapsible-content-wrapper {
    transition: width 0.2s ease 0s;
    overflow: hidden;
    padding: 5px 0; /* removes letter clipping */

    a {
      text-decoration: none;
      color: ${colors.white};
      font-size: 15px;
      padding: 0 3px 0 20px;
      white-space: nowrap;
      ${({ isRebrand }) => (isRebrand ? mediumInter() : medium())}
    }

    &.hidden {
      width: 0 !important;
    }
  }

  button {
    height: 100%;
    width: 31px;
    padding: 0;
    border: none;
    background-color: transparent;

    svg {
      height: 11px;
    }
  }

  ${(props) => `
    background-color: ${props.color};

    &:hover {
        background: ${getHoverColor(`${props.color}`)};
      }
    `}
`;

export default styledCtaBanner;
